import React, { Component, createRef, CSSProperties } from 'react';
import 'mapbox-gl/dist/mapbox-gl.css';
import { Map, MapRef, Marker, NavigationControl, Popup } from 'react-map-gl';
import { connect } from 'react-redux';
import mapboxgl from 'mapbox-gl';
import deviceIcon from '../../../../../assets/mapIconDevice.png';
import deviceIconNoCloudConnectivity from '../../../../../assets/mapIconDeviceNoCloudConnectivity.png';
import deviceIconAlertCloudConnectivity from '../../../../../assets/mapIconDeviceAlertCloudConnectivity.png';
import deviceIconAlertNoCloudConnectivity from '../../../../../assets/mapIconDeviceAlertNoCloudConnectivity.png';
import deviceIconAlertInProgress from '../../../../../assets/mapIconDeviceAlertInProgress.png';
import getAddressesCoordinates from '../../../../../redux/addressesCoordinates/actions';
import {
  HandlingErrorWrappedProps,
  withHandlingErrors,
} from '../../../../../handlingErrors';
import { getPolyglot, withPolyglot } from '../../../../../i18n';
import './Map.css';
import Polyglot from 'node-polyglot';
import { RootState, useAppSelector } from '../../../../../redux/store.model';
import {
  DEVICE_STATUS_TYPE,
  Device,
} from '../../../../../redux/commander/api/device.model';
import DeviceModel from '../../../../../redux/commander/api/DeviceModel';
import { Subtract } from 'utility-types';
import useDevicesByParams from '../../../../../redux/commander/hooks/useDevicesByParams';
import _ from 'lodash';
import useCommanderStatus from '../../../../../redux/commander/commander-status/hooks/useCommanderStatus';
import { getAlarmActiveBySelector } from '../../../../../redux/incident/selectors/getAlarmActiveBySelector';
import DeviceCloudConnectivityIcon from '../../../Device/DeviceOverview/components/DeviceActions/DeviceCloudConnectivityIcon';
import DeviceAlarmsAction from '../../../Device/DeviceOverview/components/DeviceActions/DeviceAlarmsAction';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from '@mui/material';
import { getFeatureToggle } from '../../../../../featureToggle';
import { getDictionaryGroups } from '../../../../../redux/groups/selectors';

const mapboxToken =
  'pk.eyJ1IjoiZHNlcm5haGlndWl0YSIsImEiOiJjanp0ajVoaXEwMHllM2NsMmhkbHU3cXNzIn0.gi85dWmTZgd3xlcgNCy2ww';
/* The stuttgart longitude and latitude are used to center the map, in the case
that no devices are defined */
const LONGITUDE_STUTTGART = 9.18;
const LATITUDE_STUTTGART = 48.77;

type AddressCoordinate = any;
type Coordinate = any;
interface MarkerType {
  id: any;
  coordinates: Coordinate[];
  devices: (Device & { coordinates: Coordinate[] })[];
}
type Props = {
  polyglot: Polyglot;
  devices: Device[];
  accessToken: string;
  addressesCoordinates: AddressCoordinate[];
  getAddressesCoordinates: (devices: Device[]) => unknown;
  mapStyle?: CSSProperties;
  disabled?: boolean;
  displayControls?: boolean;
} & HandlingErrorWrappedProps;

interface State {
  viewport: {
    latitude: number;
    longitude: number;
    zoom: number;
  };
  markers: MarkerType[];
  focusedMarker?: MarkerType | null;
}

/**
 * Get center
 * This function returns the coordinates where the map should be centered
 * @param array longitudes: array with all the device's longitudes
 * @param array latitudes: array with all the device's latitudes
 * @return object [centerLongitud, centerLatitude]
 */
export const getCenter = (longitudes: number[], latitudes: number[]) => {
  /* Definition map: center [longitude (horizontal), latitud(vertical)] */
  if (longitudes.length === 0 || latitudes.length === 0) {
    return [LONGITUDE_STUTTGART, LATITUDE_STUTTGART];
  }
  longitudes.sort((a, b) => a - b);
  latitudes.sort((a, b) => a - b);
  const centerLongitud =
    (longitudes[0] + longitudes[longitudes.length - 1]) / 2;
  const centerLatitude = (latitudes[0] + latitudes[latitudes.length - 1]) / 2;
  return [centerLongitud, centerLatitude];
};

/**
 * Get zoom
 * @param array longitudes
 * @return integer zoom: [0, 16]
 */
export const getZoom = (longitudes: number[]) => {
  if (longitudes.length === 0) {
    return 1;
  }
  longitudes.sort((a, b) => a - b);
  /* calculates zoom */
  let zoom =
    Math.ceil(
      (180 - (longitudes[longitudes.length - 1] - longitudes[0])) / 22.5
    ) - 3;
  zoom = zoom < 0 ? 0 : zoom;
  if (longitudes[longitudes.length - 1] - longitudes[0] < 1) {
    zoom = 16;
  }
  return zoom;
};

/**
 * Calculate viewport
 * @param array markers
 * @return object { latitude, longitude, zoom }
 */
const calculateViewport = (markers: MarkerType[]) => {
  const longitudes: number[] = [];
  const latitudes: number[] = [];

  markers.forEach((m: MarkerType) => {
    longitudes.push(m.coordinates[0].center[0]);
    latitudes.push(m.coordinates[0].center[1]);
  });

  const zoom = getZoom(longitudes);
  const [longitude, latitude] = getCenter(longitudes, latitudes);
  return { latitude, longitude, zoom };
};

/**
 * Get device icon
 * @param string state
 * @param url cloudConnectivity
 * @return url icon
 */
const getDeviceIcon = (state: string | undefined, cloudConnectivity: any) => {
  let icon = deviceIcon;
  /* select icon to be shown */
  if (state === DEVICE_STATUS_TYPE.ALERT) {
    icon = cloudConnectivity
      ? deviceIconAlertCloudConnectivity
      : deviceIconAlertNoCloudConnectivity;
  } else if (state === DEVICE_STATUS_TYPE.INPROGRESS) {
    icon = deviceIconAlertInProgress;
  } else if (state === DEVICE_STATUS_TYPE.OK_INPROGRESS) {
    icon = deviceIconAlertInProgress;
  } else if (state === DEVICE_STATUS_TYPE.OK) {
    icon = cloudConnectivity ? deviceIcon : deviceIconNoCloudConnectivity;
  }

  return icon;
};

function MapDeviceIcon(props: { device: Device }): JSX.Element {
  const { device } = props;
  if (!device) {
    return <img src={deviceIcon} className="marker-icon" alt="" />;
  }
  // Device status
  const polyglot = getPolyglot();
  // Device status
  const { commanderStatus } = useCommanderStatus(device.uuid);
  const cloudConnectivityStatus = new DeviceModel().cloudConnectivityStatus(
    device?.lastPing,
    commanderStatus
  );
  // Device Alarms
  const { data: alarmsActive } = useAppSelector((state) =>
    getAlarmActiveBySelector(state, {
      deviceId: device.uuid,
    })
  );
  const status = alarmsActive.length
    ? DEVICE_STATUS_TYPE.ALERT
    : DEVICE_STATUS_TYPE.OK;

  return (
    <img
      src={getDeviceIcon(status, cloudConnectivityStatus.cloudConnectivity)}
      className="marker-icon"
      alt={polyglot.t('map.marker_icon')}
    />
  );
}

export class MapComponent extends Component<Props, State> {
  mapRef: React.RefObject<MapRef>;

  constructor(props: Props) {
    super(props);
    this.updateViewport = this.updateViewport.bind(this);
    this.loadData = this.loadData.bind(this);
    this.setBounds = this.setBounds.bind(this);
    this.mapRef = createRef();
    this.state = {
      viewport: {
        latitude: LATITUDE_STUTTGART,
        longitude: LONGITUDE_STUTTGART,
        zoom: 8,
      },
      markers: [],
    };
  }

  componentDidMount() {
    this.loadData();
  }

  componentDidUpdate(prevProps: Props) {
    // Rendering the component only if
    // passed props value is changed
    // validate if any of devices has a different id
    if (
      this.props.devices.length !== prevProps.devices.length ||
      _.xor(
        this.props.devices.map((d) => d.uuid),
        prevProps.devices.map((d) => d.uuid)
      ).length
    ) {
      this.loadData();
    }
  }

  /**
   * Get device markers
   * @return array markerComponents
   */
  getDeviceMarkers() {
    const { markers } = this.state;
    const markerComponents: JSX.Element[] = [];

    markers.forEach((marker, index) => {
      markerComponents.push(
        <Marker
          key={`marker__${marker.id ?? index}`}
          latitude={marker.coordinates[0].center[1]}
          longitude={marker.coordinates[0].center[0]}
          anchor="bottom"
          onClick={() => this.markerClicked(marker)}
        >
          <MapDeviceIcon device={marker.devices[0]} />
        </Marker>
      );
    });

    return markerComponents;
  }

  /**
   * Get popup
   * @return object|null
   */
  getPopup() {
    const { focusedMarker } = this.state;
    const { accessToken, polyglot } = this.props;
    if (!focusedMarker) {
      return null;
    }
    return (
      <Popup
        latitude={focusedMarker.coordinates[0].center[1]}
        longitude={focusedMarker.coordinates[0].center[0]}
        closeButton={true}
        closeOnClick={false}
        onClose={() => this.setState({ focusedMarker: null })}
      >
        <div className="device-popup-wrapper">
          {focusedMarker.devices.map((device) => (
            <DeviceMarkerPopup
              device={device}
              accessToken={accessToken}
              key={`popup__${device.uuid}`}
            />
          ))}
        </div>
      </Popup>
    );
  }

  /* Get the navigation controls */
  getNavigationControls() {
    return (
      <NavigationControl
        position="bottom-right"
        showCompass={false}
        style={{ borderRadius: '1.5rem' }}
      />
    );
  }

  /**
   * Set bounds
   * Fits the map to the bounds of the markers
   * @param array markers
   */
  setBounds(markers: MarkerType[]) {
    const map = this.mapRef.current?.getMap();
    if (!map || !markers.length) return;

    const bounds = new mapboxgl.LngLatBounds();
    markers.forEach((marker) =>
      bounds.extend([
        marker.coordinates[0].center[0],
        marker.coordinates[0].center[1],
      ])
    );

    const { _ne, _sw } = bounds;

    map.fitBounds(
      [
        [_sw?.lng, _sw?.lat],
        [_ne?.lng, _ne?.lat],
      ],
      {
        animate: false,
        padding: 80,
      }
    );
    if (markers.length === 1) {
      // If only one marker, change the zoom so it is more visible
      map.setZoom(8);
    }
  }

  /**
   * Marker clicked
   * @param marker
   */
  markerClicked(marker: MarkerType) {
    this.setState({ focusedMarker: marker });
  }

  updateViewport(viewport: State['viewport']) {
    this.setState({ viewport });
  }

  /**
   * Load data
   * this function load the coordinates of the devices
   */
  async loadData() {
    const { handlingErrorsApi } = this.props;
    try {
      const { devices = [] } = this.props;
      await this.props.getAddressesCoordinates(devices);

      /* addressesCoordinates has a dictionary with addresses and coordinates */
      const { addressesCoordinates } = this.props;

      /* Create an array with the devices where coordinates are founded */
      let devicesWithCoordinates = devices
        .map((d) => {
          const entry = addressesCoordinates.find(
            (dC) =>
              dC.zipcode === (d.zipcode ? d.zipcode.trim() : '') &&
              dC.street === (d.street ? d.street.trim() : '') &&
              dC.country === (d.country ? d.country.trim() : '') &&
              dC.city === (d.city ? d.city.trim() : '')
          );
          return {
            ...d,
            coordinates: entry?.coordinates,
          };
        })
        .filter((d) => d.coordinates);
      /* group devices in the same address { coordinates, devices: [] } */
      const markers: MarkerType[] = devicesWithCoordinates
        .map((d) => {
          const devices = devicesWithCoordinates.filter(
            (dD) =>
              dD.coordinates[0].center[0] === d.coordinates[0].center[0] &&
              dD.coordinates[0].center[1] === d.coordinates[0].center[1]
          );

          devicesWithCoordinates = devicesWithCoordinates.filter(
            (dD) =>
              dD.coordinates[0].center[0] !== d.coordinates[0].center[0] &&
              dD.coordinates[0].center[1] !== d.coordinates[0].center[1]
          );
          if (devices.length > 0) {
            return {
              coordinates: devices[0].coordinates,
              devices,
            };
          }
          return null;
        })
        .filter((m) => !!m) as MarkerType[];
      const viewport = calculateViewport(markers);
      this.setState({ markers, viewport });
      this.setBounds(markers);
    } catch (error) {
      handlingErrorsApi(error);
    }
  }

  render() {
    const { viewport } = this.state;
    const { mapStyle, disabled } = this.props;
    const markers = this.getDeviceMarkers();
    const popup = this.getPopup();
    const navigationControls = this.getNavigationControls();

    return (
      <Map
        style={{ width: '100%', height: '68vh', ...mapStyle }}
        mapStyle="mapbox://styles/mapbox/streets-v9"
        mapboxAccessToken={mapboxToken}
        initialViewState={{ ...viewport }}
        // disable drag and scroll zoom
        scrollZoom={!disabled}
        dragPan={!disabled}
        ref={this.mapRef}
      >
        {markers}
        {popup}
        {this.props.displayControls && navigationControls}
      </Map>
    );
  }
}
export interface DevicesComponentProps {
  devices: Device[];
}

export function withDevices<P extends DevicesComponentProps>(
  WrappedComponent: React.ComponentType<P>
) {
  const WrappedComponentWithDevices = (
    props: Subtract<P, DevicesComponentProps>
  ) => {
    const { devices } = useDevicesByParams();
    return <WrappedComponent {...(props as P)} devices={devices} />;
  };

  return WrappedComponentWithDevices;
}
interface DeviceMarkerPopupProps {
  device: Device;
  accessToken: string;
}
function DeviceMarkerPopup(props: DeviceMarkerPopupProps) {
  const { device } = props;
  const polyglot = getPolyglot();

  const navigate = useNavigate();
  const { groupId } = useParams();

  const icon = DeviceCloudConnectivityIcon({
    device,
    groupId,
    variant: 'icon',
  });

  const featureToggle = useAppSelector(getFeatureToggle);
  const myGroups = useAppSelector(getDictionaryGroups);
  const deviceInGroup = Object.values(myGroups)?.find((g) =>
    g?.devices?.includes(device.uuid)
  );

  const openDeviceDetail = () => {
    let url = `/devicemanager/device/${device.uuid}/device-detail`;
    if (groupId) {
      url = `/devicemanager/device/${device.uuid}/group/${groupId}/device-detail`;
    }

    if (featureToggle.styledComponentsVariants === 'fresh-watch') {
      if (deviceInGroup) {
        url = `/merchandiser/${deviceInGroup.parentGroupId}`;
      } else {
        url = `/merchandiser/${groupId}`;
      }
    }
    navigate(url);
  };

  const isComposableDevice =
    featureToggle.composableDevice &&
    deviceInGroup?.composableDevice !== undefined;

  const deviceCard = (
    <Button className="content" onClick={openDeviceDetail} variant="text">
      <div>
        <img
          src={DeviceModel.getDeviceIcon(device.iconURL)}
          className="icon-device"
          alt={polyglot.t('map.icon_device')}
        />
      </div>
      <div>
        <p className="popup-device-name">
          {isComposableDevice ? deviceInGroup?.name : device.name}
        </p>
        {device.street ? device.street : ''}{' '}
        {device.zipcode ? device.zipcode : ''} <br />
        {device.city ? device.city : ''}
      </div>
    </Button>
  );

  return (
    <div className={`popup-map-device`}>
      <div className="icons-bar">
        <DeviceAlarmsAction {...props} />
        {icon}
      </div>
      {deviceCard}
    </div>
  );
}

const mapStateToProps = (state: RootState) => ({
  addressesCoordinates: state.addressesCoordinates.addressesCoordinates,
  accessToken: state.auth.accessToken,
});

export default connect(mapStateToProps, { getAddressesCoordinates })(
  withDevices(withPolyglot(withHandlingErrors(MapComponent)))
);
